import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password === password.toLowerCase()) { setError('Password must include at least one uppercase letter.'); return; }
    if (!password.split('').some(c => c >= '0' && c <= '9')) { setError('Password must include at least one number.'); return; }
    if (!password.split('').some(c => '!@#$%^&*()_+-=[]{}|;:,.<>?'.includes(c))) { setError('Password must include at least one special character (!@#$% etc).'); return; }

    setLoading(true);
    try {
      await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        body: { token, password },
      });
      navigate('/login', { state: { passwordReset: true } });
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logoRow}>
            <div style={styles.logoMark}>M</div>
            <span style={styles.logoText}>MasterFileAI</span>
          </div>
          <div style={styles.errorBox}>Invalid or missing reset token. Please request a new reset link.</div>
          <p style={styles.footerText}><Link to="/forgot-password" style={styles.link}>Request new link</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <div style={styles.logoMark}>M</div>
          <span style={styles.logoText}>MasterFileAI</span>
        </div>
        <h1 style={styles.heading}>Set new password</h1>
        <p style={styles.sub}>Choose a strong password for your account.</p>
        {error && <div style={styles.errorBox}>{error}</div>}
        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>New password</label>
          <input
            style={styles.input}
            type="password"
            placeholder="8+ chars, uppercase, number, special (!@#$%)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoFocus
          />
          <label style={styles.label}>Confirm password</label>
          <input
            style={styles.input}
            type="password"
            placeholder="Repeat your new password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            required
          />
          <button type="submit" style={styles.btn} disabled={loading}>
            {loading ? 'Saving…' : 'Set new password'}
          </button>
        </form>
        <p style={styles.footerText}>
          <Link to="/login" style={styles.link}>← Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f8f9fb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', fontFamily: "'Inter', system-ui, sans-serif" },
  card: { background: '#ffffff', border: '1px solid #e2e6ef', borderRadius: 16, padding: '48px 40px', width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' },
  logoRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 },
  logoMark: { width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #6c63ff, #3ecfcf)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18 },
  logoText: { color: '#111827', fontWeight: 700, fontSize: 18, letterSpacing: '-0.3px' },
  heading: { color: '#111827', fontSize: 22, fontWeight: 700, margin: '0 0 12px', letterSpacing: '-0.3px' },
  sub: { color: '#6b7280', fontSize: 14, margin: '0 0 28px' },
  errorBox: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, color: '#f87171', fontSize: 13, padding: '10px 14px', marginBottom: 20 },
  form: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#9ca3af', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 10 },
  input: { background: '#f8f9fb', border: '1px solid #e2e6ef', borderRadius: 8, color: '#111827', fontSize: 14, padding: '11px 14px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  btn: { marginTop: 20, background: 'linear-gradient(135deg, #6c63ff, #3ecfcf)', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 15, fontWeight: 600, padding: '13px', width: '100%' },
  footerText: { color: '#6b7280', fontSize: 13, textAlign: 'center', marginTop: 24 },
  link: { color: '#6c63ff', textDecoration: 'none', fontWeight: 500 },
};
